import { useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn, Avatar, AvatarImage, AvatarFallback } from "@tellescope/ui";
import { formatInitials } from "@tellescope/utils";
import { useVideoCalling } from "../hooks/useVideoCallingContext";
import { useMeetingOperations } from "../hooks/useMeetingOperations";
import { VideoControls } from "./VideoControls";
import { ParticipantGrid } from "./ParticipantGrid";
import type { VideoRoomViewProps } from "../types";

/**
 * VideoRoomView - Active video call interface.
 *
 * Displays participant video grid and control bar.
 */
export function VideoRoomView({
  userRole,
  showEndForAll = false,
  localUser,
  resolveParticipantIdentity,
}: VideoRoomViewProps) {
  const { t } = useTranslation("videoCalling");
  const videoCalling = useVideoCalling();
  const { leaveMeeting, endMeeting } = useMeetingOperations();
  const localVideoRef = useRef<HTMLVideoElement>(null);

  const { participants, localVideoTrack, isVideoEnabled } = videoCalling;

  // Get initials from user name, or fall back to "You"
  const initials = useMemo(() => {
    if (localUser?.fname || localUser?.lname) {
      return formatInitials(localUser);
    }
    return t("You");
  }, [localUser, t]);

  // Attach local video track to video element
  useEffect(() => {
    if (localVideoTrack && localVideoRef.current) {
      localVideoTrack.attach(localVideoRef.current);
      return () => {
        localVideoTrack.detach(localVideoRef.current!);
      };
    }
  }, [localVideoTrack]);

  const handleLeave = () => {
    leaveMeeting();
  };

  const handleEndForAll = async () => {
    try {
      await endMeeting();
    } catch (error) {
      console.error("Failed to end meeting:", error);
    }
  };

  const hasParticipants = participants.length > 0;

  return (
    <div className="flex flex-col flex-1 bg-gray-900">
      {/* Video grid area */}
      <div className="flex-1 relative overflow-hidden">
        {/* Local video - full screen when alone, PIP when others present */}
        <div
          className={cn(
            "overflow-hidden bg-gray-800",
            hasParticipants ? "absolute bottom-4 right-4 w-40 h-28 rounded-lg shadow-lg z-10" : "absolute inset-0"
          )}
        >
          <video
            ref={localVideoRef}
            autoPlay
            muted
            playsInline
            className={cn("w-full h-full object-cover scale-x-[-1]", !isVideoEnabled && "hidden")}
          />
          {!isVideoEnabled && (
            <div className="flex items-center justify-center w-full h-full bg-gray-800">
              {hasParticipants ? (
                // Smaller avatar for PIP mode
                <div className="relative">
                  <Avatar className="w-12 h-12 shadow">
                    {localUser?.avatar && <AvatarImage src={localUser.avatar} alt={initials} />}
                    <AvatarFallback className="bg-gray-200 text-gray-700 text-xl font-medium">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border border-gray-800" />
                </div>
              ) : (
                // Larger avatar when no remote participants
                <div className="relative w-1/2 max-w-[200px] aspect-square">
                  <Avatar className="w-full h-full shadow-lg">
                    {localUser?.avatar && <AvatarImage src={localUser.avatar} alt={initials} />}
                    <AvatarFallback className="bg-gray-200 text-gray-700 text-[4rem] font-medium">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute top-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-800" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Remote participants grid or waiting message overlay */}
        {hasParticipants ? (
          <ParticipantGrid participants={participants} resolveParticipantIdentity={resolveParticipantIdentity} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-gray-400 bg-black/50 px-4 py-2 rounded-lg">{t("Waiting for others to join...")}</span>
          </div>
        )}
      </div>

      {/* Control bar */}
      <VideoControls
        userRole={userRole}
        showEndForAll={showEndForAll}
        onLeave={handleLeave}
        onEndForAll={handleEndForAll}
      />
    </div>
  );
}
